import React, { useEffect, useState } from "react";
import { Button, TextField, Box, Typography } from "@mui/material";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import MasterViewController from "../../redux/actions/masterApiActions";
import FormDrawer from "../../components/layout/FormDrawer";
import PermissionsTab from "../Employees/tabs/PermissionsForm";
import api from "../../services/api";
import "../../styles/styles.css";
import "../../styles/layout.css";

function RolesFormModal({ show, handleClose, initialData = null }) {
  const dispatch = useDispatch();
  const { addRole, updateRole } = MasterViewController();
  const isEdit = Boolean(initialData?.id);

  const [permissionModules, setPermissionModules] = useState([]);

  useEffect(() => {
    if (show) {
      api.get("permission-modules/")
        .then(res => {
          if (res.data && res.data.modules) {
            setPermissionModules(res.data.modules);
          }
        })
        .catch(err => console.error("Failed to load permissions", err));
    }
  }, [show]);

  const [initialValues, setInitialValues] = useState({
    role_name: "",
    permissions: []
  });

  useEffect(() => {
    if (show && permissionModules.length > 0) {
      // Create default empty permissions map for all modules
      const defaultPerms = permissionModules.map(mod => {
        const p = { module: mod.key, moduleLabel: mod.label, enabled: false };
        mod.actions.forEach(act => p[act.key] = false);
        return p;
      });

      if (initialData) {
        // Merge initialData.permissions into defaultPerms
        const initialPerms = initialData.permissions || [];
        const mergedPerms = defaultPerms.map(defaultP => {
          const found = initialPerms.find(p => p.module === defaultP.module);
          if (found) {
            // Determine if enabled
            const enabled = modHasAnyAction(found, permissionModules.find(m => m.key === defaultP.module));
            return { ...defaultP, ...found, enabled };
          }
          return defaultP;
        });
        
        setInitialValues({
          role_name: initialData.role_name || "",
          permissions: mergedPerms
        });
      } else {
        setInitialValues({
          role_name: "",
          permissions: defaultPerms
        });
      }
    }
  }, [show, initialData, permissionModules]);

  // Helper to check if any action is enabled
  const modHasAnyAction = (permObj, modDef) => {
    if (!modDef) return false;
    return modDef.actions.some(act => permObj[act.key]);
  };

  const handleSubmit = (values, { resetForm }) => {
    const action = isEdit
      ? updateRole(initialData.id, values)
      : addRole(values);

    dispatch(action)
      .then(() => {
        resetForm();
        handleClose();
      })
      .catch((error) => {
        console.error("Error submitting role form:", error);
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, handleChange, handleBlur, submitForm, setFieldValue }) => (
        <Form style={{ display: "contents" }}>
          <FormDrawer
            open={show}
            onClose={handleClose}
            width={850}
            title={isEdit ? "Edit Role" : "Add Role"}
            subtitle={isEdit ? "Update role details" : "Add a new role to the system"}
            footer={
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, width: "100%" }}>
                <Button onClick={handleClose} variant="outlined" color="secondary">
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  variant="contained" 
                  color="primary" 
                  onClick={() => submitForm()}
                >
                  {isEdit ? "Update" : "Submit"}
                </Button>
              </Box>
            }
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
              <TextField
                label="Role Name *"
                name="role_name"
                value={values.role_name}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                variant="outlined"
              />

              {permissionModules.length > 0 && values.permissions.length > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Module Permissions</Typography>
                  <PermissionsTab
                    modulesData={permissionModules}
                    permissions={values.permissions}
                    setPermissions={(newPerms) => {
                      const nextPerms = typeof newPerms === "function" ? newPerms(values.permissions) : newPerms;
                      setFieldValue("permissions", nextPerms);
                    }}
                  />
                </Box>
              )}
            </Box>
          </FormDrawer>
        </Form>
      )}
    </Formik>
  );
}

export default RolesFormModal;
